import {
  Badge,
  Center,
  Divider,
  Flex,
  Grid,
  Loader,
  Progress,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { IQueryPosition } from "../../../../interfaces/IQuery";
import BoxFlex from "./BoxFlex";
import { usePositionGraphic } from "../../../../hooks/usePositions";
import { IResponsePositionGraphic } from "../../../../interfaces/OrderPostions";
import { Graphic } from "./Graphic";
import { useTranslation } from "react-i18next";

export const colors = {
  color: [
    "#FF6A00",
    "#822FD3",
    "#1E90FF",
    "#2ECC71",
    "#D72638",
    "#FFB800",
    "#0057D9",
    "#E65C00",
    "#A855F7",
    "#00A8E8",
  ],
  opacity: [
    "#FF6A0040",
    "#822FD340",
    "#1E90FF40",
    "#2ECC7140",
    "#D7263840",
    "#FFB80040",
    "#0057D940",
    "#E65C0040",
    "#A855F740",
    "#00A8E840",
  ],
};

interface IProps {
  query: IQueryPosition;
}

export default function GraphicPosition({ query }: IProps) {
  const theme = useMantineTheme();
  const { t } = useTranslation();
  const { data, isLoading } = usePositionGraphic(query);
  const { items } = !!data ? data : ({} as IResponsePositionGraphic);

  return (
    <Flex
      direction="column"
      p={{ base: 16, 991: 32 }}
      style={{
        borderRadius: "12px",
        border: "2px solid #F0F0F0",
        position: "relative",
      }}
      gap="16px"
    >
      <Text
        fz={{ base: 20 }}
        fw={{ base: 600 }}
        lh={{ base: "32px" }}
        color="basic.5"
      >
        {t("operacoes.resumo_das_posicoes.actives")}
      </Text>
      {isLoading ? (
        <Center w="100%" h="200px">
          <Loader color={theme.colors.blue[6]} />
        </Center>
      ) : !!data && items?.length > 0 ? (
        <Flex
          align={{ base: "center", 991: "center", 1640: "start" }}
          justify={"space-between"}
          gap="24px"
          w="100%"
          direction={{ base: "column", 991: "column", 1640: "row" }}
        >
          {!!data ? <Graphic data={data} /> : null}
          <Flex
            w="100%"
            align={{ base: "flex-end", 991: "center" }}
            justify="center"
            direction="column"
            gap={{ base: 16 }}
          >
            {items.map((item, index) => {
              const progressCopyParseFloat = parseFloat(item?.orders_success);
              const progressNoCopyParseFloat = parseFloat(item?.orders_fail);
              const total = progressCopyParseFloat + progressNoCopyParseFloat;
              const progressCopy = isNaN(progressCopyParseFloat)
                ? 0
                : (progressCopyParseFloat * 100) / total;
              const progressNoCopy = isNaN(progressNoCopyParseFloat)
                ? 0
                : (progressNoCopyParseFloat * 100) / total;

              return (
                <Flex
                  key={`${item?.product_title}-${index}`}
                  direction={{ base: "column", 1300: "row", 1640: "column" }}
                  gap="12px"
                  sx={{
                    borderRadius: "12px",
                    border: `1px solid ${theme.colors.basic[2]}`,
                  }}
                  p={{ base: 16 }}
                  w="100%"
                >
                  <Grid w="100%">
                    <Grid.Col span={"content"}>
                      <BoxFlex
                        miw={{ base: 114 }}
                        text={t("operacoes.resumo_das_posicoes.active")}
                        value={item?.symbol}
                      />
                    </Grid.Col>
                    <Grid.Col span={"auto"}>
                      <BoxFlex
                        miw={{ base: 140 }}
                        text={t("operacoes.resumo_das_posicoes.accumulated")}
                        value={item?.profit_loss_text}
                      />
                    </Grid.Col>
                    <Grid.Col span={"content"}>
                      <BoxFlex
                        miw={{ base: 75 }}
                        text={t("operacoes.resumo_das_posicoes.orders")}
                        value={item?.qtd_orders}
                      />
                    </Grid.Col>
                    <Grid.Col span={"content"}>
                      <BoxFlex
                        miw={{ base: 75 }}
                        text={t("operacoes.resumo_das_posicoes.positions")}
                        value={item?.qtd_positions}
                      />
                    </Grid.Col>
                    <Grid.Col span={"content"} offset={0}>
                      <Badge
                        h={48}
                        miw={140}
                        p={"8px 18px"}
                        radius={8}
                        bg={colors.opacity[index]}
                        styles={{
                          inner: {
                            fontFamily: "Barlow",
                            fontSize: "16px",
                            fontWeight: 600,
                            lineHeight: "32px",
                            color: colors.color[index],
                          },
                        }}
                      >
                        {item?.product_title}
                      </Badge>
                    </Grid.Col>
                  </Grid>
                  <Flex
                    direction={"column"}
                    gap="12px"
                    w={{ base: "100%", 1300: "50%", 1640: "100%" }}
                  >
                    <Progress
                      radius="12px"
                      size={24}
                      sections={[
                        { value: progressCopy, color: "#169C3C" },
                        { value: progressNoCopy, color: "#E3183B" },
                      ]}
                    />
                    <Flex
                      justify={"space-between"}
                      align={"center"}
                      w={"100%"}
                      wrap="wrap"
                      gap={{ base: "8px", 500: "24px" }}
                      direction={{ base: "column", 500: "row" }}
                    >
                      <Flex gap="8px" align="center" h="22px">
                        <div
                          style={{
                            height: "8px",
                            width: "8px",
                            backgroundColor: "#169C3C",
                            borderRadius: "50%",
                          }}
                        />
                        <Text fz={"14px"} fw={500}>
                          {t("operacoes.resumo_das_posicoes.copied")}
                        </Text>
                        <Divider orientation="vertical" size="sm" />
                        <Text fz={"14px"} fw={700}>
                          {item?.orders_success}
                        </Text>
                      </Flex>
                      <Flex gap="8px" align="center" h="22px">
                        <div
                          style={{
                            height: "8px",
                            width: "8px",
                            backgroundColor: "#E3183B",
                            borderRadius: "50%",
                          }}
                        />
                        <Text fz={"14px"} fw={500}>
                          {t("operacoes.resumo_das_posicoes.not_copied")}
                        </Text>
                        <Divider orientation="vertical" size="sm" />
                        <Text fz={"14px"} fw={700}>
                          {item?.orders_fail}
                        </Text>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              );
            })}
          </Flex>
        </Flex>
      ) : (
        <Center w="100%" h="200px">
          {t("global.pagination.no_data")}
        </Center>
      )}
    </Flex>
  );
}
